import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { consumeSupportReturn } from '../lib/supportHandoffReturn';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        toast.error('Invalid authentication session');
        navigate('/login');
        return;
      }

      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/google-session`, {
          method: 'POST',
          headers: {
            'X-Session-ID': sessionId,
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error('Authentication failed');
        }

        const data = await response.json();
        setUser(data.user);

        toast.success('Successfully logged in!');

        // Route to the right dashboard based on role — kept in lock-step
        // with the same logic in LoginPage.js so Google sign-in and
        // email/password sign-in always land on the same dashboard.
        const adminRoles = ['admin', 'company_manager'];
        // Supervisor + crew_leader + agent + site_agent + staff all share
        // the StaffDashboard scaffold (with role-aware sub-tabs).
        const driverRoles = ['manager', 'driver'];
        const siteAgentRoles = ['supervisor', 'crew_leader', 'agent', 'site_agent', 'staff'];
        const role = data.user.role;
        const emailLc = (data.user.email || '').toLowerCase();
        const isCompanyDomain = emailLc.endsWith('@nobleempirecs.com');

        let destination = '/tenant-dashboard';
        if (['resident', 'tenant'].includes(role)) {
          // Residents / Tenants app social sign-in lands back in the mobile app shell.
          destination = '/resident';
        } else if (adminRoles.includes(role) || isCompanyDomain) {
          destination = '/company-manager-dashboard';
        } else if (role === 'property_manager') {
          destination = '/property-manager-dashboard';
        } else if (driverRoles.includes(role)) {
          destination = '/driver-dashboard';
        } else if (siteAgentRoles.includes(role)) {
          destination = '/staff-dashboard';
        }

        navigate(consumeSupportReturn() || destination, { state: { user: data.user }, replace: true });
      } catch (error) {
        console.error('Auth error:', error);
        toast.error('Authentication failed. Please try again.');
        navigate('/login');
      }
    };

    processSession();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#FFD60A]"></div>
        <p className="mt-4 text-white">Authenticating...</p>
      </div>
    </div>
  );
}